import _axios, {
  AxiosResponse,
  AxiosRequestConfig,
  AxiosError,
  AxiosInstance,
} from 'axios'

import { Errors } from '../types/common'
import store from '../../index'
import { getClientVersion } from './auth'
import version from '../../clientVersion' // yarn dev(stg, prod)時に自動作成される

class ApiClient {
  static create_without_token(): AxiosInstance {
    const axios = _axios.create({
      baseURL: process.env.API_URL,
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      validateStatus: (status) => (status >= 200 && status < 300) || status === 302,
      responseType: 'json',
      withCredentials: true,
    })

    axios.interceptors.request.use(
      (config) => this.requestSuccess(config),
      (config) => this.requestFailure(config),
    )
    axios.interceptors.response.use(
      (config) => this.responseSuccess(config),
      (config) => this.responseFailure(config),
    )
    return axios
  }

  static create(): AxiosInstance {
    this.checkVersion()
    const axios = this.create_without_token()
    axios.defaults.headers.Authorization = `Bearer ${store.getState().login.accessToken}`
    return axios
  }

  static createGetRequestWithBody(): AxiosInstance {
    const axios = _axios.create({
      baseURL: process.env.API_URL,
      headers: {
        'Content-Type': 'application/csv',
        'X-Requested-With': 'XMLHttpRequest',
        'X-HTTP-Method-Override': 'Get',
      },
      validateStatus: (status) => (status >= 200 && status < 300) || status === 302,
      responseType: 'json',
      withCredentials: true,
    })

    axios.interceptors.request.use(
      (config) => this.requestSuccess(config),
      (config) => this.requestFailure(config),
    )
    axios.interceptors.response.use(
      (config) => this.responseSuccess(config),
      (config) => this.responseFailure(config),
    )

    axios.defaults.headers.Authorization = `Bearer ${store.getState().login.accessToken}`
    return axios
  }

  static createPostRequestWithFile(contentType: string): AxiosInstance {
    const axios = _axios.create({
      baseURL: process.env.API_URL,
      headers: {
        'Content-Type': contentType,
        'X-Requested-With': 'XMLHttpRequest',
      },
      validateStatus: (status) => (status >= 200 && status < 300) || status === 302,
      responseType: 'json',
      withCredentials: true,
    })

    axios.interceptors.request.use(
      (config) => this.requestSuccess(config),
      (config) => this.requestFailure(config),
    )
    axios.interceptors.response.use(
      (config) => this.responseSuccess(config),
      (config) => this.responseFailure(config),
    )

    axios.defaults.headers.Authorization = `Bearer ${store.getState().login.accessToken}`
    return axios
  }

  static create_self(): AxiosInstance {
    const axios = _axios.create({
      baseURL: process.env.APP_URL,
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      validateStatus: (status) => (status >= 200 && status < 300) || status === 302,
      responseType: 'json',
      withCredentials: true,
    })

    axios.interceptors.request.use(
      (config) => this.requestSuccess(config),
      (config) => this.requestFailure(config),
    )
    axios.interceptors.response.use(
      (config) => this.responseSuccess(config),
      (config) => this.responseFailure(config),
    )
    return axios
  }

  static checkVersion(): void {
    getClientVersion()
      .then((result) => {
        if (version < result.version) {
          location.reload()
        }
      })
  }

  static requestSuccess(config: AxiosRequestConfig): AxiosRequestConfig {
    if (process.env.NODE_ENV !== 'production') {
      this.logger('// REQUEST SUCCESS', config)
    }
    // Request(成功)時の共通処理があればココに書く
    return config
  }

  static requestFailure(config: AxiosRequestConfig): Promise<never> {
    if (process.env.NODE_ENV !== 'production') {
      this.logger('// REQUEST FAILURE', config)
    }
    // Request(失敗)時の共通処理があればココに書く
    return Promise.reject(config)
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static responseSuccess(config: AxiosResponse<any>): AxiosResponse<any> {
    if (process.env.NODE_ENV !== 'production') {
      this.logger('// RESPONSE SUCCESS', config)
    }
    // Response(成功)時の共通処理があればココに書く
    return config
  }

  static responseFailure(config: AxiosError): Promise<never> {
    if (process.env.NODE_ENV !== 'production') {
      this.logger('// RESPONSE FAILURE', config)
    }

    const { response } = config
    const errorData: Errors = {
      status: response?.status,
      needResetToken: false,
      errors: [],
    }

    switch (response?.status) {
      case 401:
        // ログインセッションが切れている場合の処理
        errorData.needResetToken = true
        errorData.errors.push({
          message: ['.auth_error'],
        })
        break
      case 403:
        break
      case 409:
        errorData.errors.push({
          message: ['.plan_error'],
        })
        break
      case 422:
        // バリデーションエラー
        if (response?.data) {
          if (Array.isArray(response.data.errors)) {
            errorData.errors.push(...response.data.errors)
          } else {
            errorData.errors.push(response.data.errors)
          }
        } else {
          errorData.errors.push({
            message: ['.validation_error'],
          })
        }
        break
      default:
        if (response?.data) {
          errorData.errors.push(...response.data.errors)
        } else {
          errorData.errors.push({
            message: ['.unexpected_error'],
          })
        }
    }
    return Promise.reject(errorData)
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static logger(label: string, res: AxiosRequestConfig | AxiosResponse<any> | AxiosError): void {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.groupCollapsed(label)
      // eslint-disable-next-line no-console
      console.dir(res)
      // eslint-disable-next-line no-console
      console.groupEnd()
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/explicit-module-boundary-types
  static get(url: string, callback: (result: any) => void, option?: any): Promise<any> {
    return this.create()
      .get(url, { params: option })
      .then((response) => callback(response.data))
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.log('ERROR!! occurred in Backend.')
        return Promise.reject(error)
      })
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/explicit-module-boundary-types
  static getWithFile(url: string, callback: (result: any) => void, file: string): Promise<any> {
    return this.createGetRequestWithBody()
      .post(url, file)
      .then((response) => callback(response.data))
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.log('ERROR!! occurred in Backend.')
        return Promise.reject(error)
      })
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/explicit-module-boundary-types
  static get_without_token(url: string, callback: (result: any) => void, option?: any): Promise<any> {
    return this.create_without_token()
      .get(url, { params: option })
      .then((response) => callback(response.data))
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.log('ERROR!! occurred in Backend.')
        return Promise.reject(error)
      })
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/explicit-module-boundary-types
  static get_self(url: string, callback: (result: any) => void, option?: any): Promise<any> {
    return this.create_self()
      .get(url, { params: option })
      .then((response) => callback(response.data))
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.log('ERROR!! occurred in Backend.')
        return Promise.reject(error)
      })
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/explicit-module-boundary-types
  static getFile(url: string, callback: (result: any) => void, option?: any): Promise<any> {
    return this.create()
      .get(url, { params: option, responseType: "blob" })
      .then((response) => callback(response))
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.log('ERROR!! occurred in Backend.')
        return Promise.reject(error)
      })
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/explicit-module-boundary-types
  static post(url: string, callback: (result: any) => void, option?: any): Promise<any> {
    return this.create()
      .post(url, option)
      .then((response) => callback(response.data))
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.log('ERROR!! occurred in Backend.')
        return Promise.reject(error)
      })
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/explicit-module-boundary-types
  static post_without_token(url: string, callback: (result: any) => void, option?: any): Promise<any> {
    return this.create_without_token()
      .post(url, option)
      .then((response) => callback(response.data))
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.log('ERROR!! occurred in Backend.')
        return Promise.reject(error)
      })
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/explicit-module-boundary-types
  static postWithFile(url: string, callback: (result: any) => void, file: string): Promise<any> {
    return this.createPostRequestWithFile("application/csv")
      .post(url, file)
      .then((response) => callback(response.data))
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.log('ERROR!! occurred in Backend.')
        return Promise.reject(error)
      })
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/explicit-module-boundary-types
  static postWithMulti(url: string, callback: (result: any) => void, option?: any): Promise<any> {
    return this.createPostRequestWithFile("multipart/form-data")
      .post(url, option)
      .then((response) => callback(response.data))
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.log('ERROR!! occurred in Backend.')
        return Promise.reject(error)
      })
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/explicit-module-boundary-types
  static patch(url: string, callback: (result: any) => void, option?: any): Promise<any> {
    return this.create()
      .patch(url, option)
      .then((response) => callback(response.data))
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.log('ERROR!! occurred in Backend.')
        return Promise.reject(error)
      })
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/explicit-module-boundary-types
  static delete(url: string, callback: (result: any) => void, option?: any): Promise<any> {
    return this.create()
      .delete(url, option)
      .then((response) => callback(response.data))
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.log('ERROR!! occurred in Backend.')
        return Promise.reject(error)
      })
  }
}

export default ApiClient
